import { useEffect, useState, useCallback, useMemo } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import {
    Plus,
    Building2,
    Briefcase,
    ArrowRight,
    ArrowLeft,
    Upload,
    FileSpreadsheet,
    X,
    AlertCircle,
    Check,
    Save,
    MapPin,
} from "lucide-react";
import { PageLayout } from "@/ui/layouts";
import { Card, CardContent, Button, Loader, Modal, DialogFooter, Pagination } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import { MultiSelect } from "@/ui/molecules/MultiSelect";
import { DealsTable } from "@/ui/organisms";
import { cn } from "@/utils/utils";
import { getNow } from "@/utils/dateService";
import type { TAsyncState, TDeal, TPaginatedDeals } from "@/store";
import { useServerTable } from "@/useCases/useServerTable";
import {
    textParam,
    selectParam,
    booleanParam,
    dateRangeParam,
    sortParam,
} from "@/useCases/serverTableQuery";
import type { TServerTableQueryInput } from "@/useCases/useServerTable";
import type {
    TCreateDealRequest,
    TApiDealFileType,
    TApiGlType,
    TApiFinalFileDestination,
    TApiSegment2MappingBehavior,
    TApiSegment4MappingBehavior,
    TApiSegment5MappingBehavior,
    TApiSegment6MappingBehavior,
    TApiSegment7MappingBehavior,
    TApiSegmentMappingValidationResponse,
    TApiPropcodeMappingValidationResponse,
    TApiColumnExtractionResponse,
    TApiColumnMappingFileType,
    TApiColumnMappingFieldType,
    TCreateColumnMappingRequest,
} from "@/apis/proda_finance";

// Simple types for dropdowns
type TServiceProviderOption = { id: string; name: string; code?: string };
type TPortfolioCompanyOption = { id: string; name: string; code?: string };
type TUserOption = { id: string; display_name: string; email: string };

type TFilterOptions = {
    serviceProviders: Array<{ id: string; name: string }>;
    portfolioCompanies: Array<{ id: string; name: string }>;
};

type TDealsQuery = {
    skip?: number;
    limit?: number;
    service_provider_id?: string;
    portfolio_company_id?: string;
    business_partner_id?: string;
    name?: string;
    deal_file_type?: string;
    is_active?: boolean;
    created_from?: string;
    created_to?: string;
    sort_by?: string;
    sort_dir?: string;
};

const DEALS_PAGE_SIZE = 50;

/**
 * Maps server-table state (column filters, sorting, page) to the deals list
 * query params. Module-level and exported so it can be exercised directly in
 * tests without rendering the view (see createViewDeals.test.tsx).
 */
export const dealsToQuery = (input: TServerTableQueryInput): TDealsQuery => {
    const created = dateRangeParam(input.columnFilters, "createdAt");
    return {
        skip: (input.page - 1) * DEALS_PAGE_SIZE,
        limit: DEALS_PAGE_SIZE,
        // "businessPartnerName" is the combined column (option values are SP or
        // PC ids); business_partner_id matches whichever side the deal has.
        business_partner_id: selectParam(input.columnFilters, "businessPartnerName"),
        portfolio_company_id: selectParam(input.columnFilters, "portfolioCompanyId"),
        name: textParam(input.columnFilters, "name"),
        deal_file_type: selectParam(input.columnFilters, "dealFileType"),
        is_active: booleanParam(input.columnFilters, "isActive"),
        created_from: created?.from,
        created_to: created?.to,
        ...sortParam(input.sorting),
    };
};

/**
 * View parameters (injected dependencies)
 */
export type TViewDealsParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useDealsState: () => TAsyncState<TPaginatedDeals>;
        useServiceProviders: () => { data: TServiceProviderOption[] | null; loading: boolean };
        usePortfolioCompanies: () => { data: TPortfolioCompanyOption[] | null; loading: boolean };
        useUsers: () => { data: TUserOption[] | null; loading: boolean };
        onDealClick: (deal: TDeal) => void;
        onCreateDeal: (data: TCreateDealRequest) => Promise<void>;
        onExtractColumns: (
            file: File,
            headerRowNumber: number,
        ) => Promise<TApiColumnExtractionResponse>;
        onValidateSegmentMappingFile: (
            segmentNumber: number,
            file: File,
        ) => Promise<TApiSegmentMappingValidationResponse>;
        onValidatePropcodeMappingFile: (
            file: File,
        ) => Promise<TApiPropcodeMappingValidationResponse>;
        onPageChange: (page: number) => void;
        onRefresh: () => void;
        canCreateDeal: boolean;
        loadDeals: (query: TDealsQuery, signal?: AbortSignal) => void;
        useFilterOptions: () => TFilterOptions;
        canEditDeal: boolean;
        onToggleDealActive: (deal: TDeal) => Promise<void>;
    };
};

/**
 * View props (from router/parent)
 */
export type TViewDealsProps = Record<string, never>;

import {
    TB_COLUMN_FIELDS,
    GL_COLUMN_FIELDS,
} from "@/ui/organisms/OrganismColumnMappings/columnFieldConfigs";
import {
    SegmentMappingUploadSection,
    PropcodeMappingUploadSection,
    SEGMENT_CONFIGS,
    PROPCODE_REQUIRED_COLUMNS,
    dealFileTypes,
    finalDestinations,
    segment2MappingTypes,
    segment4MappingTypes,
    segment5MappingTypes,
    segment6MappingTypes,
    segment7MappingTypes,
} from "@/ui/organisms/DealWizard";
import type {
    TSegmentNumber,
    TSegmentMappingData,
    TPropcodeMappingData,
} from "@/ui/organisms/DealWizard";

// ViewDeals uses a stricter `headerRowNumber: number` (no empty-string sentinel).
// This local copy preserves that; modal will be replaced by <DealWizard> in a later step.
type TColumnMappingData = {
    file: File | null;
    headerRowNumber: number;
    columns: string[];
    mappings: Record<TApiColumnMappingFieldType, string>;
    error: string | null;
    warnings: string[];
    isLoading: boolean;
};

const emptyColumnMappingData: TColumnMappingData = {
    file: null,
    headerRowNumber: 1,
    columns: [],
    mappings: {} as Record<TApiColumnMappingFieldType, string>,
    error: null,
    warnings: [],
    isLoading: false,
};

// Refactor 1.x migration status:
//   1.1-1.4 Shared constants and upload sections migrated.
//   1.9 Modal -> <DealWizard mode="modal"> migration is STAGED. The 4-step modal
//   below remains intact for behavior parity; the building blocks at
//   @/ui/organisms/DealWizard are ready to replace it once visual/BDD parity
//   has been verified.

/**
 * Creates the Deals list view component
 */
export const createViewDeals = ({ useCases }: TViewDealsParams) => {
    const ViewDeals: React.FC<TViewDealsProps> = () => {
        const {
            onMount,
            onUnmount,
            useDealsState,
            useServiceProviders,
            usePortfolioCompanies,
            useUsers,
            onDealClick,
            onCreateDeal,
            onExtractColumns,
            onValidateSegmentMappingFile,
            onValidatePropcodeMappingFile,
            onPageChange: _onPageChange,
            onRefresh,
            canCreateDeal,
            loadDeals,
            useFilterOptions,
            canEditDeal,
            onToggleDealActive,
        } = useCases();

        const dealsState = useDealsState();
        const serviceProviders = useServiceProviders();
        const portfolioCompanies = usePortfolioCompanies();
        const users = useUsers();
        const filterOptions = useFilterOptions();

        const server = useServerTable({
            pageSize: DEALS_PAGE_SIZE,
            toQuery: dealsToQuery,
            fetchData: loadDeals,
        });

        const spOptions = useMemo(
            () => filterOptions.serviceProviders.map((sp) => ({ id: sp.id, name: sp.name })),
            [filterOptions.serviceProviders],
        );
        const pcOptions = useMemo(
            () => filterOptions.portfolioCompanies.map((pc) => ({ id: pc.id, name: pc.name })),
            [filterOptions.portfolioCompanies],
        );

        // Create deal modal state
        const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [error, setError] = useState<string | null>(null);
        // Steps: 1=Initial Info, 2=Column Mapping, 3=Segment Mapping, 4=Propcode Mapping
        const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
        const [activeSegmentTab, setActiveSegmentTab] = useState<TSegmentNumber>(2);

        // Form state
        const currentDate = getNow();
        const [formData, setFormData] = useState({
            name: "",
            code: "",
            service_provider_id: "",
            portfolio_company_id: "",
            finance_user_ids: [] as string[],
            deal_file_type: "tb_and_gl" as TApiDealFileType,
            gl_type: "" as string,
            monthly_deadline_day: 15,
            final_file_destination: "yardi" as TApiFinalFileDestination,
            start_period_month: currentDate.getMonth() + 1, // 1-12
            start_period_year: currentDate.getFullYear(),
            segment_2_mapping_type: "no_mapping" as TApiSegment2MappingBehavior,
            segment_4_mapping_type: "no_mapping" as TApiSegment4MappingBehavior,
            segment_5_mapping_type: "no_mapping" as TApiSegment5MappingBehavior,
            segment_6_mapping_type: "no_mapping" as TApiSegment6MappingBehavior,
            segment_7_mapping_type: "no_mapping" as TApiSegment7MappingBehavior,
        });

        // Column mapping state (Step 2)
        const [tbColumnMapping, setTbColumnMapping] =
            useState<TColumnMappingData>(emptyColumnMappingData);
        const [glColumnMapping, setGlColumnMapping] =
            useState<TColumnMappingData>(emptyColumnMappingData);

        // Segment mapping data state (Step 3)
        const [segmentMappings, setSegmentMappings] = useState<
            Record<TSegmentNumber, TSegmentMappingData>
        >({
            2: { file: null, rows: [], error: null, warnings: [], isLoading: false },
            4: { file: null, rows: [], error: null, warnings: [], isLoading: false },
            5: { file: null, rows: [], error: null, warnings: [], isLoading: false },
            6: { file: null, rows: [], error: null, warnings: [], isLoading: false },
            7: { file: null, rows: [], error: null, warnings: [], isLoading: false },
        });

        // Propcode mapping state (Step 4)
        const [propcodeMappings, setPropcodeMappings] = useState<TPropcodeMappingData>({
            file: null,
            rows: [],
            error: null,
            warnings: [],
            isLoading: false,
        });

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const handleToggleActive = useCallback(
            async (deal: TDeal) => {
                // Rejections propagate to the DealStatusCell confirmation modal.
                await onToggleDealActive(deal);
                onRefresh();
            },
            [onToggleDealActive, onRefresh],
        );

        // Check if segment is enabled (mapping type != no_mapping)
        const isSegmentEnabled = useCallback(
            (segment: TSegmentNumber): boolean => {
                const mappingType =
                    formData[`segment_${segment}_mapping_type` as keyof typeof formData];
                return mappingType !== "no_mapping";
            },
            [formData],
        );

        // Get enabled segments
        const enabledSegments = ([2, 4, 5, 6, 7] as TSegmentNumber[]).filter(isSegmentEnabled);

        // Check if all enabled segments have valid uploads
        const allEnabledSegmentsHaveUploads = enabledSegments.every(
            (seg) =>
                segmentMappings[seg].file !== null &&
                segmentMappings[seg].rows.length > 0 &&
                !segmentMappings[seg].error,
        );

        // Can proceed to next wizard step (all enabled segments have uploads)
        const canProceedWithDeal = enabledSegments.length === 0 || allEnabledSegmentsHaveUploads;

        // Check if column mapping is complete for a file type
        const isColumnMappingComplete = useCallback(
            (mapping: TColumnMappingData, fileType: TApiColumnMappingFileType): boolean => {
                if (mapping.columns.length === 0) return false;

                const fields = fileType === "tb" ? TB_COLUMN_FIELDS : GL_COLUMN_FIELDS;
                const requiredFields = fields.filter((f) => {
                    if (f.conditionalSegment) {
                        return f.required && isSegmentEnabled(f.conditionalSegment);
                    }
                    if (f.fieldType === "period" && fileType === "gl") {
                        return formData.gl_type === "ytd";
                    }
                    return f.required;
                });

                return requiredFields.every((f) => {
                    const value = mapping.mappings[f.fieldType];
                    return value && value.trim() !== "";
                });
            },
            [isSegmentEnabled, formData.gl_type],
        );

        // Is TB and GL
        const isTbAndGl = formData.deal_file_type === "tb_and_gl";

        // Can proceed from column mapping step
        const canProceedFromColumnMapping =
            isColumnMappingComplete(tbColumnMapping, "tb") &&
            (!isTbAndGl || isColumnMappingComplete(glColumnMapping, "gl"));

        // Handle column file selection - extracts column headers from Excel
        const handleColumnFileSelect = useCallback(
            async (fileType: TApiColumnMappingFileType, file: File, headerRowNumber: number) => {
                const setMapping = fileType === "gl" ? setGlColumnMapping : setTbColumnMapping;

                // Validate file extension
                if (!file.name.toLowerCase().endsWith(".xlsx")) {
                    setMapping({
                        file: null,
                        headerRowNumber: 1,
                        columns: [],
                        mappings: {} as Record<TApiColumnMappingFieldType, string>,
                        error: "Only XLSX files are supported",
                        warnings: [],
                        isLoading: false,
                    });
                    return;
                }

                // Set loading state
                setMapping((prev) => ({
                    ...prev,
                    file,
                    headerRowNumber,
                    error: null,
                    isLoading: true,
                }));

                try {
                    // Send to backend for column extraction
                    const result = await onExtractColumns(file, headerRowNumber);

                    if (!result.success) {
                        setMapping((prev) => ({
                            ...prev,
                            file: null,
                            columns: [],
                            mappings: {} as Record<TApiColumnMappingFieldType, string>,
                            error: result.errors.join("; ") || "Column extraction failed",
                            warnings: result.warnings,
                            isLoading: false,
                        }));
                    } else {
                        setMapping((prev) => ({
                            ...prev,
                            file,
                            headerRowNumber,
                            columns: result.columns,
                            mappings: prev.mappings, // Preserve existing mappings
                            error: null,
                            warnings: result.warnings,
                            isLoading: false,
                        }));
                    }
                } catch (err) {
                    setMapping((prev) => ({
                        ...prev,
                        file: null,
                        columns: [],
                        mappings: {} as Record<TApiColumnMappingFieldType, string>,
                        error: err instanceof Error ? err.message : "Failed to extract columns",
                        warnings: [],
                        isLoading: false,
                    }));
                }
            },
            [onExtractColumns],
        );

        // Handle column mapping change
        const handleColumnMappingChange = useCallback(
            (
                fileType: TApiColumnMappingFileType,
                fieldType: TApiColumnMappingFieldType,
                columnName: string,
            ) => {
                const setMapping = fileType === "gl" ? setGlColumnMapping : setTbColumnMapping;
                setMapping((prev) => ({
                    ...prev,
                    mappings: {
                        ...prev.mappings,
                        [fieldType]: columnName,
                    },
                }));
            },
            [],
        );

        // Clear column file
        const handleClearColumnFile = useCallback((fileType: TApiColumnMappingFileType) => {
            const setMapping = fileType === "gl" ? setGlColumnMapping : setTbColumnMapping;
            setMapping(emptyColumnMappingData);
        }, []);

        // Handle file selection for a segment - sends to backend for validation
        const handleSegmentFileSelect = useCallback(
            async (segment: TSegmentNumber, file: File) => {
                // Validate file extension
                if (!file.name.toLowerCase().endsWith(".xlsx")) {
                    setSegmentMappings((prev) => ({
                        ...prev,
                        [segment]: {
                            file: null,
                            rows: [],
                            error: "Only XLSX files are supported",
                            warnings: [],
                            isLoading: false,
                        },
                    }));
                    return;
                }

                // Set loading state
                setSegmentMappings((prev) => ({
                    ...prev,
                    [segment]: { file, rows: [], error: null, warnings: [], isLoading: true },
                }));

                try {
                    // Send to backend for validation
                    const result = await onValidateSegmentMappingFile(segment, file);

                    if (!result.success) {
                        setSegmentMappings((prev) => ({
                            ...prev,
                            [segment]: {
                                file: null,
                                rows: [],
                                error: result.errors.join("; ") || "Validation failed",
                                warnings: result.warnings,
                                isLoading: false,
                            },
                        }));
                    } else {
                        setSegmentMappings((prev) => ({
                            ...prev,
                            [segment]: {
                                file,
                                rows: result.mappings,
                                error: null,
                                warnings: result.warnings,
                                isLoading: false,
                            },
                        }));
                    }
                } catch (err) {
                    setSegmentMappings((prev) => ({
                        ...prev,
                        [segment]: {
                            file: null,
                            rows: [],
                            error: err instanceof Error ? err.message : "Failed to validate file",
                            warnings: [],
                            isLoading: false,
                        },
                    }));
                }
            },
            [onValidateSegmentMappingFile],
        );

        // Clear file for a segment
        const handleClearSegmentFile = useCallback((segment: TSegmentNumber) => {
            setSegmentMappings((prev) => ({
                ...prev,
                [segment]: { file: null, rows: [], error: null, warnings: [], isLoading: false },
            }));
        }, []);

        // Handle propcode file selection and validation - sends to backend
        const handlePropcodeFileSelect = useCallback(
            async (file: File) => {
                // Validate file extension
                if (!file.name.toLowerCase().endsWith(".xlsx")) {
                    setPropcodeMappings({
                        file: null,
                        rows: [],
                        error: "Only XLSX files are supported",
                        warnings: [],
                        isLoading: false,
                    });
                    return;
                }

                // Set loading state
                setPropcodeMappings({
                    file,
                    rows: [],
                    error: null,
                    warnings: [],
                    isLoading: true,
                });

                try {
                    // Send to backend for validation
                    const result = await onValidatePropcodeMappingFile(file);

                    if (!result.success) {
                        setPropcodeMappings({
                            file: null,
                            rows: [],
                            error: result.errors.join("; ") || "Validation failed",
                            warnings: result.warnings,
                            isLoading: false,
                        });
                    } else {
                        setPropcodeMappings({
                            file,
                            rows: result.mappings.map((m) => ({
                                level: m.level,
                                source_propcode: m.source_propcode,
                                target_propcode: m.target_propcode,
                            })),
                            error: null,
                            warnings: result.warnings,
                            isLoading: false,
                        });
                    }
                } catch (err) {
                    setPropcodeMappings({
                        file: null,
                        rows: [],
                        error: err instanceof Error ? err.message : "Failed to validate file",
                        warnings: [],
                        isLoading: false,
                    });
                }
            },
            [onValidatePropcodeMappingFile],
        );

        // Clear propcode file
        const handleClearPropcodeFile = useCallback(() => {
            setPropcodeMappings({
                file: null,
                rows: [],
                error: null,
                warnings: [],
                isLoading: false,
            });
        }, []);

        const resetForm = () => {
            const now = getNow();
            setFormData({
                name: "",
                code: "",
                service_provider_id: "",
                portfolio_company_id: "",
                finance_user_ids: [],
                deal_file_type: "tb_and_gl",
                gl_type: "",
                monthly_deadline_day: 15,
                final_file_destination: "yardi",
                start_period_month: now.getMonth() + 1,
                start_period_year: now.getFullYear(),
                segment_2_mapping_type: "no_mapping",
                segment_4_mapping_type: "no_mapping",
                segment_5_mapping_type: "no_mapping",
                segment_6_mapping_type: "no_mapping",
                segment_7_mapping_type: "no_mapping",
            });
            setTbColumnMapping(emptyColumnMappingData);
            setGlColumnMapping(emptyColumnMappingData);
            setSegmentMappings({
                2: { file: null, rows: [], error: null, warnings: [], isLoading: false },
                4: { file: null, rows: [], error: null, warnings: [], isLoading: false },
                5: { file: null, rows: [], error: null, warnings: [], isLoading: false },
                6: { file: null, rows: [], error: null, warnings: [], isLoading: false },
                7: { file: null, rows: [], error: null, warnings: [], isLoading: false },
            });
            setPropcodeMappings({
                file: null,
                rows: [],
                error: null,
                warnings: [],
                isLoading: false,
            });
            setError(null);
            setCurrentStep(1);
            setActiveSegmentTab(2);
            setIsCreateModalOpen(false);
        };

        const handleCreateClick = () => {
            setIsCreateModalOpen(true);
        };

        // Step 1 -> Step 2 (Initial Info -> Column Mapping)
        const handleNextFromStep1 = () => {
            setError(null);

            // Validate required fields
            if (!formData.name.trim()) {
                setError("Name is required");
                return;
            }
            if (!formData.code.trim()) {
                setError("Deal Code is required");
                return;
            }
            const hasSp = Boolean(formData.service_provider_id?.trim());
            const hasPc = Boolean(formData.portfolio_company_id?.trim());
            if (!hasSp && !hasPc) {
                setError("Deal must belong to either a Service Provider or a Portfolio Company.");
                return;
            }
            if (hasSp && hasPc) {
                setError("Deal cannot belong to both a Service Provider and a Portfolio Company.");
                return;
            }
            if (formData.finance_user_ids.length === 0) {
                setError("At least one Finance user is required");
                return;
            }
            if (formData.deal_file_type === "tb_and_gl" && !formData.gl_type) {
                setError("GL Type is required for TB and GL deals");
                return;
            }

            setCurrentStep(2);
        };

        // Step 2 -> Step 3 (Column Mapping -> Segment Mapping)
        const handleNextFromStep2 = () => {
            setError(null);
            setCurrentStep(3);
            // Set active tab to first enabled segment, or first segment if none enabled
            if (enabledSegments.length > 0) {
                setActiveSegmentTab(enabledSegments[0]!);
            }
        };

        // Step 3 -> Step 4 (Segment Mapping -> Propcode Mapping)
        const handleNextFromStep3 = () => {
            setError(null);
            setCurrentStep(4);
        };

        // Go back from Step 2 to Step 1
        const handleBackFromStep2 = () => {
            setCurrentStep(1);
            setError(null);
        };

        // Go back from Step 3 to Step 2
        const handleBackFromStep3 = () => {
            setCurrentStep(2);
            setError(null);
        };

        // Go back from Step 4 to Step 3
        const handleBackFromStep4 = () => {
            setCurrentStep(3);
            setError(null);
        };

        const handleSubmit = async () => {
            setError(null);

            // Validate at least one finance user is selected
            if (formData.finance_user_ids.length === 0) {
                setError("At least one Finance user is required");
                return;
            }

            // Validate propcode mapping file is uploaded (required)
            if (!propcodeMappings.file || propcodeMappings.rows.length === 0) {
                setError("Propcode Mapping file is required. Please upload a valid .xlsx file.");
                return;
            }

            setIsSubmitting(true);

            // Build start_period date (first day of the month)
            const startPeriod = `${formData.start_period_year}-${String(formData.start_period_month).padStart(2, "0")}-01`;

            try {
                // Build segment mappings from uploaded files
                const segmentMappingsToCreate: Array<{
                    segment_type: number;
                    source_segment_value: string;
                    source_segment_name?: string;
                    target_segment_value: string;
                }> = [];

                for (const segment of enabledSegments) {
                    const data = segmentMappings[segment];
                    if (data.file && data.rows.length > 0) {
                        for (const row of data.rows) {
                            segmentMappingsToCreate.push({
                                segment_type: segment,
                                source_segment_value: row.source_counterparty_code,
                                source_segment_name: row.source_counterparty_name || undefined,
                                target_segment_value: row.target_value,
                            });
                        }
                    }
                }

                // Build propcode mappings from uploaded file
                const propcodeMappingsToCreate: Array<{
                    level: string;
                    source_propcode: string;
                    target_propcode: string;
                }> = [];

                if (propcodeMappings.file && propcodeMappings.rows.length > 0) {
                    for (const row of propcodeMappings.rows) {
                        propcodeMappingsToCreate.push({
                            level: row.level,
                            source_propcode: row.source_propcode,
                            target_propcode: row.target_propcode,
                        });
                    }
                }

                // Build column mappings from the mapping state
                const columnMappingsToCreate: TCreateColumnMappingRequest[] = [];

                // TB column mappings
                if (tbColumnMapping.columns.length > 0) {
                    for (const [fieldType, columnName] of Object.entries(
                        tbColumnMapping.mappings,
                    )) {
                        if (columnName && columnName.trim()) {
                            columnMappingsToCreate.push({
                                file_type: "tb",
                                field_type: fieldType as TApiColumnMappingFieldType,
                                column_name: columnName,
                                header_row_number: tbColumnMapping.headerRowNumber,
                            });
                        }
                    }
                }

                // GL column mappings (only for TB and GL deals)
                if (isTbAndGl && glColumnMapping.columns.length > 0) {
                    for (const [fieldType, columnName] of Object.entries(
                        glColumnMapping.mappings,
                    )) {
                        if (columnName && columnName.trim()) {
                            columnMappingsToCreate.push({
                                file_type: "gl",
                                field_type: fieldType as TApiColumnMappingFieldType,
                                column_name: columnName,
                                header_row_number: glColumnMapping.headerRowNumber,
                            });
                        }
                    }
                }

                await onCreateDeal({
                    name: formData.name,
                    code: formData.code.toUpperCase(),
                    service_provider_id: formData.service_provider_id,
                    portfolio_company_id: formData.portfolio_company_id,
                    finance_user_ids: formData.finance_user_ids,
                    deal_file_type: formData.deal_file_type,
                    gl_type: (formData.gl_type as TApiGlType) || undefined,
                    monthly_deadline_day: formData.monthly_deadline_day,
                    final_file_destination: formData.final_file_destination,
                    start_period: startPeriod,
                    segment_2_mapping_type: formData.segment_2_mapping_type,
                    segment_4_mapping_type: formData.segment_4_mapping_type,
                    segment_5_mapping_type: formData.segment_5_mapping_type,
                    segment_6_mapping_type: formData.segment_6_mapping_type,
                    segment_7_mapping_type: formData.segment_7_mapping_type,
                    column_mappings:
                        columnMappingsToCreate.length > 0 ? columnMappingsToCreate : undefined,
                    segment_mappings:
                        segmentMappingsToCreate.length > 0 ? segmentMappingsToCreate : undefined,
                    propcode_mappings:
                        propcodeMappingsToCreate.length > 0 ? propcodeMappingsToCreate : undefined,
                });
                resetForm();
                onRefresh();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to create deal");
            } finally {
                setIsSubmitting(false);
            }
        };

        const deals = dealsState.data?.items ?? [];
        const isLoading = dealsState.status === "pending";

        return (
            <PageLayout
                title="Deals"
                description="Manage deals and their configurations"
                actions={
                    canCreateDeal ? (
                        <Button onClick={handleCreateClick}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Deal
                        </Button>
                    ) : undefined
                }
            >
                <Card>
                    <CardContent className="pt-6">
                        {/* Full-page loader only before first data: swapping the table
                            out on refresh would unmount an open status-confirm modal. */}
                        {isLoading && deals.length === 0 ? (
                            <div className="flex justify-center py-12">
                                <Loader size="lg" />
                            </div>
                        ) : deals.length === 0 && server.columnFilters.length === 0 ? (
                            <EmptyState
                                variant="default"
                                title="No deals yet"
                                description="Create your first deal to start processing financial data."
                                action={
                                    canCreateDeal
                                        ? {
                                              label: "Create Deal",
                                              onClick: handleCreateClick,
                                          }
                                        : undefined
                                }
                            />
                        ) : (
                            <DealsTable
                                deals={deals}
                                loading={isLoading}
                                onDealClick={onDealClick}
                                serviceProviders={spOptions}
                                portfolioCompanies={pcOptions}
                                storageKey="deals"
                                columnFilters={server.columnFilters}
                                onColumnFiltersChange={server.onColumnFiltersChange}
                                sortingState={server.sorting}
                                onSortingChange={server.onSortingChange}
                                rowCount={buildRowCount({
                                    total: dealsState.data?.total ?? 0,
                                    totalUnfiltered: dealsState.data?.totalUnfiltered,
                                    hasActiveFilters: server.columnFilters.length > 0,
                                })}
                                canToggleActive={canEditDeal}
                                onToggleActive={handleToggleActive}
                            />
                        )}
                        <Pagination
                            page={server.page}
                            pageSize={DEALS_PAGE_SIZE}
                            total={dealsState.data?.total ?? 0}
                            onPageChange={server.setPage}
                            className="px-6 py-4 border-t"
                        />
                    </CardContent>
                </Card>

                {/* Create Deal Modal */}
                <Modal
                    open={isCreateModalOpen}
                    onOpenChange={(open) => !open && resetForm()}
                    title={
                        currentStep === 1
                            ? "Create Deal - Step 1: Deal Information"
                            : currentStep === 2
                              ? "Create Deal - Step 2: Column Mapping"
                              : currentStep === 3
                                ? "Create Deal - Step 3: Segment Mapping"
                                : "Create Deal - Step 4: Propcode Mapping"
                    }
                    description={
                        currentStep === 1
                            ? "Set up deal information"
                            : currentStep === 2
                              ? "Map Excel column headers to data fields"
                              : currentStep === 3
                                ? "Upload segment mapping files"
                                : "Upload propcode mapping file (required)"
                    }
                    className="max-w-4xl"
                >
                    <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                        {error && (
                            <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                                {error}
                            </div>
                        )}

                        {currentStep === 1 ? (
                            /* Step 1: Deal Required Info */
                            <>
                                {/* Basic Information */}
                                <div className="space-y-3">
                                    <h3 className="text-sm font-medium">Basic Information</h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Name <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        name: e.target.value,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                placeholder="Q1 2026 Trial Balance"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Deal Code{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.code}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        code: e.target.value
                                                            .toUpperCase()
                                                            .replace(/[^A-Z0-9_-]/g, ""),
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary uppercase"
                                                placeholder="DEAL_001"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Relationships */}
                                <div className="space-y-3">
                                    <h3 className="text-sm font-medium flex items-center gap-2">
                                        <Building2 className="h-4 w-4" />
                                        Relationships
                                    </h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Service Provider{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.service_provider_id}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        service_provider_id: e.target.value,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                <option value="">
                                                    Select a service provider...
                                                </option>
                                                {serviceProviders.data?.map((sp) => (
                                                    <option key={sp.id} value={sp.id}>
                                                        {sp.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Portfolio Company{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.portfolio_company_id}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        portfolio_company_id: e.target.value,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                <option value="">
                                                    Select a portfolio company...
                                                </option>
                                                {portfolioCompanies.data?.map((pc) => (
                                                    <option key={pc.id} value={pc.id}>
                                                        {pc.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-xs font-medium mb-1">
                                                Finance Team{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <MultiSelect
                                                options={
                                                    users.data?.map((u) => ({
                                                        id: u.id,
                                                        label: `${u.display_name} (${u.email})`,
                                                    })) || []
                                                }
                                                selectedIds={formData.finance_user_ids}
                                                onChange={(ids) =>
                                                    setFormData({
                                                        ...formData,
                                                        finance_user_ids: ids,
                                                    })
                                                }
                                                placeholder="Select finance team members..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Configuration */}
                                <div className="space-y-3">
                                    <h3 className="text-sm font-medium flex items-center gap-2">
                                        <Briefcase className="h-4 w-4" />
                                        Configuration
                                    </h3>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                File Type{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.deal_file_type}
                                                onChange={(e) => {
                                                    const newFileType = e.target
                                                        .value as TApiDealFileType;
                                                    setFormData({
                                                        ...formData,
                                                        deal_file_type: newFileType,
                                                        gl_type:
                                                            newFileType !== "tb_and_gl"
                                                                ? "none"
                                                                : formData.gl_type,
                                                    });
                                                }}
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {dealFileTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {formData.deal_file_type === "tb_and_gl" && (
                                            <div>
                                                <label className="block text-xs font-medium mb-1">
                                                    GL Type{" "}
                                                    <span className="text-destructive">*</span>
                                                </label>
                                                <select
                                                    value={formData.gl_type}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            gl_type: e.target.value as TApiGlType,
                                                        })
                                                    }
                                                    className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                >
                                                    <option value="">Select GL type...</option>
                                                    <option value="ytd">YTD</option>
                                                    <option value="mtd">MTD</option>
                                                </select>
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Final Destination{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.final_file_destination}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        final_file_destination: e.target
                                                            .value as TApiFinalFileDestination,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {finalDestinations.map((dest) => (
                                                    <option key={dest.value} value={dest.value}>
                                                        {dest.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Start Period Month{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.start_period_month}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        start_period_month: parseInt(
                                                            e.target.value,
                                                        ),
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                <option value={1}>January</option>
                                                <option value={2}>February</option>
                                                <option value={3}>March</option>
                                                <option value={4}>April</option>
                                                <option value={5}>May</option>
                                                <option value={6}>June</option>
                                                <option value={7}>July</option>
                                                <option value={8}>August</option>
                                                <option value={9}>September</option>
                                                <option value={10}>October</option>
                                                <option value={11}>November</option>
                                                <option value={12}>December</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Start Period Year{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.start_period_year}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        start_period_year: parseInt(e.target.value),
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {Array.from({ length: 10 }, (_, i) => {
                                                    const year = getNow().getFullYear() - 2 + i;
                                                    return (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Deadline Day{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.monthly_deadline_day}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        monthly_deadline_day:
                                                            parseInt(e.target.value) || 1,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                min={1}
                                                max={28}
                                            />
                                        </div>
                                    </div>

                                    {/* Segment Mapping Types */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Segment 2 Mapping Type{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.segment_2_mapping_type}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        segment_2_mapping_type: e.target
                                                            .value as TApiSegment2MappingBehavior,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {segment2MappingTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Segment 4 Mapping Type{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.segment_4_mapping_type}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        segment_4_mapping_type: e.target
                                                            .value as TApiSegment4MappingBehavior,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {segment4MappingTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Segment 5 Mapping Type{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.segment_5_mapping_type}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        segment_5_mapping_type: e.target
                                                            .value as TApiSegment5MappingBehavior,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {segment5MappingTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Segment 6 Mapping Type{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.segment_6_mapping_type}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        segment_6_mapping_type: e.target
                                                            .value as TApiSegment6MappingBehavior,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {segment6MappingTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Segment 7 Mapping Type{" "}
                                                <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                value={formData.segment_7_mapping_type}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        segment_7_mapping_type: e.target
                                                            .value as TApiSegment7MappingBehavior,
                                                    })
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                            >
                                                {segment7MappingTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : currentStep === 2 ? (
                            /* Step 2: Column Mapping */
                            <>
                                {/* Progress Indicator */}
                                <div className="flex items-center justify-between text-xs mb-4">
                                    <span className="text-muted-foreground">
                                        Creating deal:{" "}
                                        <strong className="text-foreground">{formData.name}</strong>
                                    </span>
                                    <span className="text-muted-foreground">
                                        File Type:{" "}
                                        <strong>
                                            {formData.deal_file_type === "tb_and_gl"
                                                ? "TB and GL"
                                                : "TB Only"}
                                        </strong>
                                    </span>
                                </div>

                                {/* TB Column Mapping Section */}
                                <div className="border rounded-lg p-4 space-y-4">
                                    <h3 className="text-sm font-medium flex items-center gap-2">
                                        <FileSpreadsheet className="h-4 w-4" />
                                        TB Column Mapping
                                    </h3>

                                    {/* File Upload for TB */}
                                    <div className="grid grid-cols-2 gap-3 items-end">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Header Row Number
                                            </label>
                                            <input
                                                type="number"
                                                value={tbColumnMapping.headerRowNumber}
                                                onChange={(e) =>
                                                    setTbColumnMapping((prev) => ({
                                                        ...prev,
                                                        headerRowNumber: Math.max(
                                                            1,
                                                            parseInt(e.target.value) || 1,
                                                        ),
                                                    }))
                                                }
                                                className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                min={1}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">
                                                Select file for TB Columns
                                            </label>
                                            <div className="flex gap-2">
                                                <label className="flex-1">
                                                    <input
                                                        type="file"
                                                        accept=".xlsx"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                handleColumnFileSelect(
                                                                    "tb",
                                                                    file,
                                                                    tbColumnMapping.headerRowNumber,
                                                                );
                                                            }
                                                            e.target.value = "";
                                                        }}
                                                        className="hidden"
                                                        disabled={tbColumnMapping.isLoading}
                                                    />
                                                    <div
                                                        className={cn(
                                                            "px-3 py-1.5 text-sm border rounded-lg text-center cursor-pointer transition-colors",
                                                            tbColumnMapping.isLoading
                                                                ? "opacity-50 cursor-not-allowed"
                                                                : tbColumnMapping.file
                                                                  ? "border-primary bg-primary/5"
                                                                  : "hover:border-primary/50 hover:bg-primary/5",
                                                        )}
                                                    >
                                                        {tbColumnMapping.isLoading ? (
                                                            <span className="flex items-center justify-center gap-2">
                                                                <Loader size="sm" />
                                                                Extracting...
                                                            </span>
                                                        ) : tbColumnMapping.file ? (
                                                            <span className="flex items-center justify-center gap-2">
                                                                <Check className="h-3 w-3 text-green-500" />
                                                                {tbColumnMapping.file.name}
                                                            </span>
                                                        ) : (
                                                            <span className="flex items-center justify-center gap-2">
                                                                <Upload className="h-3 w-3" />
                                                                Select file
                                                            </span>
                                                        )}
                                                    </div>
                                                </label>
                                                {tbColumnMapping.file && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleClearColumnFile("tb")}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {tbColumnMapping.error && (
                                        <div className="flex items-start gap-2 p-2 bg-destructive/10 text-destructive rounded text-xs">
                                            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                            <span>{tbColumnMapping.error}</span>
                                        </div>
                                    )}

                                    {tbColumnMapping.warnings.length > 0 && (
                                        <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded text-xs">
                                            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                            <div>
                                                <p className="font-medium">Warnings:</p>
                                                <ul className="list-disc list-inside text-xs mt-1">
                                                    {tbColumnMapping.warnings
                                                        .slice(0, 3)
                                                        .map((w, i) => (
                                                            <li key={i}>{w}</li>
                                                        ))}
                                                    {tbColumnMapping.warnings.length > 3 && (
                                                        <li>
                                                            ...and{" "}
                                                            {tbColumnMapping.warnings.length - 3}{" "}
                                                            more
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    )}

                                    {/* TB Column Mapping Fields */}
                                    {tbColumnMapping.columns.length > 0 && (
                                        <div className="border rounded-lg p-3 bg-muted/20">
                                            <h4 className="text-xs font-medium mb-2">
                                                TB Column Mappings
                                            </h4>
                                            <div className="grid grid-cols-2 gap-3">
                                                {TB_COLUMN_FIELDS.filter((field) => {
                                                    if (field.conditionalSegment) {
                                                        return isSegmentEnabled(
                                                            field.conditionalSegment,
                                                        );
                                                    }
                                                    return true;
                                                }).map((field) => (
                                                    <div key={field.fieldType}>
                                                        <label className="block text-xs font-medium mb-1">
                                                            {field.label}{" "}
                                                            {field.required && (
                                                                <span className="text-destructive">
                                                                    *
                                                                </span>
                                                            )}
                                                        </label>
                                                        <select
                                                            value={
                                                                tbColumnMapping.mappings[
                                                                    field.fieldType
                                                                ] || ""
                                                            }
                                                            onChange={(e) =>
                                                                handleColumnMappingChange(
                                                                    "tb",
                                                                    field.fieldType,
                                                                    e.target.value,
                                                                )
                                                            }
                                                            className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                        >
                                                            <option value="">
                                                                Select column...
                                                            </option>
                                                            {tbColumnMapping.columns.map((col) => (
                                                                <option key={col} value={col}>
                                                                    {col}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* GL Column Mapping Section (only for TB and GL) */}
                                {isTbAndGl && (
                                    <div className="border rounded-lg p-4 space-y-4 mt-4">
                                        <h3 className="text-sm font-medium flex items-center gap-2">
                                            <FileSpreadsheet className="h-4 w-4" />
                                            GL Column Mapping
                                        </h3>

                                        {/* File Upload for GL */}
                                        <div className="grid grid-cols-2 gap-3 items-end">
                                            <div>
                                                <label className="block text-xs font-medium mb-1">
                                                    Header Row Number
                                                </label>
                                                <input
                                                    type="number"
                                                    value={glColumnMapping.headerRowNumber}
                                                    onChange={(e) =>
                                                        setGlColumnMapping((prev) => ({
                                                            ...prev,
                                                            headerRowNumber: Math.max(
                                                                1,
                                                                parseInt(e.target.value) || 1,
                                                            ),
                                                        }))
                                                    }
                                                    className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                    min={1}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium mb-1">
                                                    Select file for GL Columns
                                                </label>
                                                <div className="flex gap-2">
                                                    <label className="flex-1">
                                                        <input
                                                            type="file"
                                                            accept=".xlsx"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) {
                                                                    handleColumnFileSelect(
                                                                        "gl",
                                                                        file,
                                                                        glColumnMapping.headerRowNumber,
                                                                    );
                                                                }
                                                                e.target.value = "";
                                                            }}
                                                            className="hidden"
                                                            disabled={glColumnMapping.isLoading}
                                                        />
                                                        <div
                                                            className={cn(
                                                                "px-3 py-1.5 text-sm border rounded-lg text-center cursor-pointer transition-colors",
                                                                glColumnMapping.isLoading
                                                                    ? "opacity-50 cursor-not-allowed"
                                                                    : glColumnMapping.file
                                                                      ? "border-primary bg-primary/5"
                                                                      : "hover:border-primary/50 hover:bg-primary/5",
                                                            )}
                                                        >
                                                            {glColumnMapping.isLoading ? (
                                                                <span className="flex items-center justify-center gap-2">
                                                                    <Loader size="sm" />
                                                                    Extracting...
                                                                </span>
                                                            ) : glColumnMapping.file ? (
                                                                <span className="flex items-center justify-center gap-2">
                                                                    <Check className="h-3 w-3 text-green-500" />
                                                                    {glColumnMapping.file.name}
                                                                </span>
                                                            ) : (
                                                                <span className="flex items-center justify-center gap-2">
                                                                    <Upload className="h-3 w-3" />
                                                                    Select file
                                                                </span>
                                                            )}
                                                        </div>
                                                    </label>
                                                    {glColumnMapping.file && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() =>
                                                                handleClearColumnFile("gl")
                                                            }
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {glColumnMapping.error && (
                                            <div className="flex items-start gap-2 p-2 bg-destructive/10 text-destructive rounded text-xs">
                                                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                                <span>{glColumnMapping.error}</span>
                                            </div>
                                        )}

                                        {glColumnMapping.warnings.length > 0 && (
                                            <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded text-xs">
                                                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                                <div>
                                                    <p className="font-medium">Warnings:</p>
                                                    <ul className="list-disc list-inside text-xs mt-1">
                                                        {glColumnMapping.warnings
                                                            .slice(0, 3)
                                                            .map((w, i) => (
                                                                <li key={i}>{w}</li>
                                                            ))}
                                                        {glColumnMapping.warnings.length > 3 && (
                                                            <li>
                                                                ...and{" "}
                                                                {glColumnMapping.warnings.length -
                                                                    3}{" "}
                                                                more
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}

                                        {/* GL Column Mapping Fields */}
                                        {glColumnMapping.columns.length > 0 && (
                                            <div className="border rounded-lg p-3 bg-muted/20">
                                                <h4 className="text-xs font-medium mb-2">
                                                    GL Column Mappings
                                                </h4>
                                                <div className="grid grid-cols-2 gap-3">
                                                    {GL_COLUMN_FIELDS.filter((field) => {
                                                        if (field.conditionalSegment) {
                                                            return isSegmentEnabled(
                                                                field.conditionalSegment,
                                                            );
                                                        }
                                                        return true;
                                                    }).map((field) => {
                                                        const isFieldRequired =
                                                            field.fieldType === "period"
                                                                ? formData.gl_type === "ytd"
                                                                : field.required;
                                                        return (
                                                            <div key={field.fieldType}>
                                                                <label className="block text-xs font-medium mb-1">
                                                                    {field.label}{" "}
                                                                    {isFieldRequired && (
                                                                        <span className="text-destructive">
                                                                            *
                                                                        </span>
                                                                    )}
                                                                </label>
                                                                <select
                                                                    value={
                                                                        glColumnMapping.mappings[
                                                                            field.fieldType
                                                                        ] || ""
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleColumnMappingChange(
                                                                            "gl",
                                                                            field.fieldType,
                                                                            e.target.value,
                                                                        )
                                                                    }
                                                                    className="w-full px-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                                                >
                                                                    <option value="">
                                                                        Select column...
                                                                    </option>
                                                                    {glColumnMapping.columns.map(
                                                                        (col) => (
                                                                            <option
                                                                                key={col}
                                                                                value={col}
                                                                            >
                                                                                {col}
                                                                            </option>
                                                                        ),
                                                                    )}
                                                                </select>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation Status */}
                                {!canProceedFromColumnMapping && (
                                    <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded text-xs mt-4">
                                        <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                        <span>
                                            Please upload files and complete all required column
                                            mappings to proceed.
                                        </span>
                                    </div>
                                )}
                            </>
                        ) : currentStep === 3 ? (
                            /* Step 3: Segment Mappings */
                            <>
                                {/* Progress Indicator */}
                                <div className="flex items-center justify-between text-xs mb-4">
                                    <span className="text-muted-foreground">
                                        Creating deal:{" "}
                                        <strong className="text-foreground">{formData.name}</strong>
                                    </span>
                                    {enabledSegments.length === 0 ? (
                                        <span className="text-muted-foreground">
                                            No segment mappings required
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground">
                                            {
                                                enabledSegments.filter(
                                                    (s) =>
                                                        segmentMappings[s].file &&
                                                        segmentMappings[s].rows.length > 0,
                                                ).length
                                            }{" "}
                                            / {enabledSegments.length} segments
                                        </span>
                                    )}
                                </div>

                                {/* Segment Tabs */}
                                <div className="border rounded-lg">
                                    {/* Tab Navigation */}
                                    <div className="flex border-b bg-muted/30">
                                        {([2, 4, 5, 6, 7] as TSegmentNumber[]).map((segment) => {
                                            const enabled = isSegmentEnabled(segment);
                                            const hasUpload =
                                                segmentMappings[segment].file !== null &&
                                                segmentMappings[segment].rows.length > 0;
                                            const hasError =
                                                segmentMappings[segment].error !== null;

                                            return (
                                                <button
                                                    key={segment}
                                                    type="button"
                                                    onClick={() =>
                                                        enabled && setActiveSegmentTab(segment)
                                                    }
                                                    disabled={!enabled}
                                                    className={cn(
                                                        "flex-1 px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px flex items-center justify-center gap-1",
                                                        enabled
                                                            ? activeSegmentTab === segment
                                                                ? "border-primary text-primary bg-background"
                                                                : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                                            : "border-transparent text-muted-foreground/50 cursor-not-allowed",
                                                    )}
                                                >
                                                    {SEGMENT_CONFIGS[segment].label}
                                                    {enabled && hasUpload && !hasError && (
                                                        <Check className="h-3 w-3 text-green-500" />
                                                    )}
                                                    {enabled && hasError && (
                                                        <AlertCircle className="h-3 w-3 text-destructive" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Tab Content */}
                                    <div className="p-4">
                                        {enabledSegments.length === 0 ? (
                                            <div className="text-center py-8 text-muted-foreground">
                                                <p className="font-medium">
                                                    No segment mappings required
                                                </p>
                                                <p className="text-xs mt-1">
                                                    All segment mapping types are set to &quot;No
                                                    Mapping&quot;.
                                                </p>
                                            </div>
                                        ) : (
                                            <SegmentMappingUploadSection
                                                config={SEGMENT_CONFIGS[activeSegmentTab]}
                                                mappingData={segmentMappings[activeSegmentTab]}
                                                onFileSelect={(file) =>
                                                    handleSegmentFileSelect(activeSegmentTab, file)
                                                }
                                                onClearFile={() =>
                                                    handleClearSegmentFile(activeSegmentTab)
                                                }
                                                disabled={!isSegmentEnabled(activeSegmentTab)}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Validation Status */}
                                {enabledSegments.length > 0 && !canProceedWithDeal && (
                                    <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded text-xs">
                                        <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                        <span>
                                            Upload files for all enabled segments:{" "}
                                            {enabledSegments
                                                .filter(
                                                    (s) =>
                                                        !segmentMappings[s].file ||
                                                        segmentMappings[s].rows.length === 0 ||
                                                        segmentMappings[s].error,
                                                )
                                                .map((s) => `Segment ${s}`)
                                                .join(", ")}
                                        </span>
                                    </div>
                                )}
                            </>
                        ) : (
                            /* Step 4: Propcode Mapping */
                            <>
                                {/* Progress Indicator */}
                                <div className="flex items-center justify-between text-xs mb-4">
                                    <span className="text-muted-foreground">
                                        Creating deal:{" "}
                                        <strong className="text-foreground">{formData.name}</strong>
                                    </span>
                                    <span className="text-muted-foreground flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        Propcode Mapping <span className="text-destructive">*</span>
                                    </span>
                                </div>

                                {/* Description */}
                                <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground mb-4">
                                    <p>
                                        <strong className="text-foreground">Required:</strong>{" "}
                                        Upload an Excel file to map Service Provider property codes
                                        to ERP/BRE property codes.
                                    </p>
                                    <p className="mt-1">
                                        Required columns:{" "}
                                        <strong>{PROPCODE_REQUIRED_COLUMNS.join(", ")}</strong>
                                    </p>
                                </div>

                                {/* Propcode Upload */}
                                <PropcodeMappingUploadSection
                                    mappingData={propcodeMappings}
                                    onFileSelect={handlePropcodeFileSelect}
                                    onClearFile={handleClearPropcodeFile}
                                />
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        {currentStep === 1 ? (
                            <>
                                <Button type="button" variant="outline" onClick={resetForm}>
                                    Cancel
                                </Button>
                                <Button type="button" onClick={handleNextFromStep1}>
                                    Next
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </>
                        ) : currentStep === 2 ? (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleBackFromStep2}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleNextFromStep2}
                                    disabled={!canProceedFromColumnMapping}
                                >
                                    Next
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </>
                        ) : currentStep === 3 ? (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleBackFromStep3}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleNextFromStep3}
                                    disabled={!canProceedWithDeal}
                                >
                                    Next
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleBackFromStep4}
                                    disabled={isSubmitting}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={
                                        isSubmitting ||
                                        propcodeMappings.error !== null ||
                                        !propcodeMappings.file ||
                                        propcodeMappings.rows.length === 0 ||
                                        propcodeMappings.isLoading
                                    }
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader size="sm" className="mr-2" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="mr-2 h-4 w-4" />
                                            Create Deal
                                        </>
                                    )}
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </Modal>
            </PageLayout>
        );
    };

    return ViewDeals;
};
